import * as React from 'react';
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../session/InternalAuthProvider';
import './CalendarSpeedDial.scss';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { AccountData } from '../../api';
import { CalendarCreateIcon, CalendarCreateIconName } from './CalendarCreateIcon';

type CalendarSpeedDialProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  setShiftDialogOpen: (open: boolean) => void;
  setNoteDialogOpen: (open: boolean) => void;
};

enum ActionType {
  addOrder = 'addOrder',
  addAvailability = 'addAvailability',
  addShift = 'addShift',
  addNote = 'addNote',
}

type Action = {
  icon: React.ReactNode;
  label: string;
  actionType: ActionType;
};
export default function CalendarSpeedDial({
  open,
  setOpen,
  setShiftDialogOpen,
  setNoteDialogOpen,
}: CalendarSpeedDialProps) {
  const { readOnly } = UseAuthPermissions();
  const { t } = useTranslation();
  // @ts-ignore
  const { authState } = useAuth();

  const account = authState.account as unknown as AccountData;
  const { openEditOrderDrawer, openExperienceAvailabilityDrawer } = useCalendar();
  const actions: Action[] = [
    {
      icon: <CalendarCreateIcon name={CalendarCreateIconName.NewOrder} />,
      label: t('add_order'),
      actionType: ActionType.addOrder,
    },
    {
      icon: <CalendarCreateIcon name={CalendarCreateIconName.Availability} />,
      label: t('calendar.add_unavailability'),
      actionType: ActionType.addAvailability,
    },
    {
      icon: <CalendarCreateIcon name={CalendarCreateIconName.Shift} />,
      label: account.shifts_as_employee_unavailability
        ? t('calendar.add_employee_unavliability')
        : t('calendar.add_shift'),
      actionType: ActionType.addShift,
    },
    {
      icon: <CalendarCreateIcon name={CalendarCreateIconName.Note} />,
      label: t('calendar.add_note'),
      actionType: ActionType.addNote,
    },
  ];

  // Add-shift was the only action employees had here, so with it gone the dial has nothing to offer.
  if (readOnly) {
    return null;
  }

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleClick = (e: any, action: Action) => {
    e.preventDefault();
    switch (action.actionType) {
      case ActionType.addAvailability:
        openExperienceAvailabilityDrawer({});
        break;
      case ActionType.addShift:
        setShiftDialogOpen(true);
        break;
      case ActionType.addOrder:
        openEditOrderDrawer({});
        break;
      case ActionType.addNote:
        setNoteDialogOpen(true);
        break;
      default:
    }
    handleClose();
  };

  return (
    <Box
      sx={{
        width: open ? '100%' : 'auto',
        height: open ? '100%' : 'auto',
        position: 'fixed',
        bottom: 0,
        insetInlineEnd: 0,
        transform: 'translateZ(0px)',
        flexGrow: 1,
        zIndex: 100,
      }}>
      <Backdrop
        open={open}
        sx={{
          width: open ? '100%' : 'auto',
          height: open ? '100%' : 'auto',
          zIndex: 100,
        }}
      />
      <SpeedDial
        className="calendar-speed-dial"
        sx={{ position: 'fixed', bottom: 16, insetInlineEnd: 16 }}
        ariaLabel="SpeedDial"
        icon={<SpeedDialIcon />}
        onClose={handleClose}
        onOpen={handleOpen}
        open={open}>
        {actions.map((action) => (
          <SpeedDialAction
            key={action.label}
            icon={action.icon}
            tooltipTitle={action.label}
            tooltipOpen
            // 48px touch targets (MUI's default is 40), with the brand icons' stroke in ink.
            FabProps={{ sx: { width: 48, height: 48, color: 'text.primary' } }}
            onClick={(e) => handleClick(e, action)}
          />
        ))}
      </SpeedDial>
    </Box>
  );
}
